/**
 * CapabilityAdvisoryPanel — Level 1 Advisory (never blocking)
 *
 * Shown inline on artifact detail views for ALL training types (free + paid).
 * Language is always advisory: "This training builds on…" / "Helpful background".
 * Renders null when no capability map entries exist.
 *
 * variant="full"    — card with header (used in detail modal)
 * variant="compact" — single-line chip row (used in recommendation cards)
 */

import { BookOpen } from 'lucide-react';
import { useArtifactCapabilityAdvisory } from '@/hooks/useArtifactCapabilityAdvisory';

interface CapabilityAdvisoryPanelProps {
  artifactId: string;
  variant?: 'compact' | 'full';
}

export function CapabilityAdvisoryPanel({
  artifactId,
  variant = 'full',
}: CapabilityAdvisoryPanelProps) {
  const { advisoryEntries, isLoading } = useArtifactCapabilityAdvisory(artifactId);

  if (isLoading || advisoryEntries.length === 0) return null;

  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-1.5 flex-wrap mt-1.5 text-xs text-muted-foreground">
        <BookOpen className="h-3 w-3 flex-shrink-0" />
        <span className="font-medium">Builds on:</span>
        {advisoryEntries.slice(0, 4).map((entry, i) => (
          <span key={entry.key}>
            {entry.label}
            {i < Math.min(advisoryEntries.length, 4) - 1 && (
              <span className="mx-0.5 opacity-50">·</span>
            )}
          </span>
        ))}
        {advisoryEntries.length > 4 && (
          <span className="opacity-50">+{advisoryEntries.length - 4} more</span>
        )}
      </div>
    );
  }

  // Full variant
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
        <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
        <span>This training builds on…</span>
      </div>
      <ul className="space-y-1">
        {advisoryEntries.map((entry) => (
          <li key={entry.key} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="h-1 w-1 rounded-full bg-muted-foreground/50 flex-shrink-0" />
            <span>
              {entry.label}
              {entry.is_primary && (
                <span className="ml-1.5 text-[10px] text-muted-foreground/70">(Primary)</span>
              )}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-muted-foreground/70 italic">
        Helpful background — not required
      </p>
    </div>
  );
}
